import React, { PropsWithChildren, useEffect } from "react";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { Container, Box, Avatar, Typography, Button } from "@mui/material";
import * as Sentry from "@sentry/react";
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useRegisterAuthTokenProvider } from "./InitApiClient";
import { Helmet } from "react-helmet-async";
import { usePostHog } from 'posthog-js/react';
import { reloadAuthActions } from "../utils/reloadAuth";

const {
  REACT_APP_AUTH0_DOMAIN,
  REACT_APP_AUTH0_CLIENT_ID,
  REACT_APP_AUTH0_API_AUDIENCE,
} = process.env

if (!REACT_APP_AUTH0_DOMAIN) throw new Error('REACT_APP_AUTH0_DOMAIN is not set')
if (!REACT_APP_AUTH0_CLIENT_ID) throw new Error('REACT_APP_AUTH0_CLIENT_ID is not set')
if (!REACT_APP_AUTH0_API_AUDIENCE) throw new Error('REACT_APP_AUTH0_API_AUDIENCE is not set')

const RegisterAuth0TokenProvider: React.FC = () => {
  const auth0 = useAuth0()
  const posthog = usePostHog()

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Logged in user changed to:', auth0.user?.email)
    Sentry.setUser({
      id: auth0.user?.sub,
      email: auth0.user?.email,
    });
    posthog?.identify(auth0.user?.sub, {
      email: auth0.user?.email,
      auth0_id: auth0.user?.sub,
    });
  }, [auth0.user, posthog])

  useRegisterAuthTokenProvider(
    'auth0',
    async () => auth0.getAccessTokenSilently(),
    [auth0],
  )

  // Setup the token reloading function
  useEffect(() => {
    const fn = async () => {
      if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Reloading the Auth0 Token')
      await auth0.getAccessTokenSilently({ cacheMode: 'off' })
    }

    reloadAuthActions.auth0Jwt = fn
    return () => {
      if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Unloading the Auth0 Token Reload Function')
      reloadAuthActions.auth0Jwt = undefined
    }
  }, [auth0])

  return null
}

const Login: React.FC = () => {
  const auth0 = useAuth0()
  const posthog = usePostHog()

  return <>
    <Helmet>
      <title>DBS Login</title>
    </Helmet>
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <img src="/branding/totalscreening/logo.jpeg" alt={`${process.env.REACT_APP_NAME_STUB} Logo`} />
        <Avatar sx={{ mt: 4, mb: 2, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Applicant Portal
        </Typography>

        <Button
          type="button"
          fullWidth
          variant="contained"
          sx={{ mt: 5, mb: 2 }}
          onClick={() => {
            posthog?.capture('login_button_clicked')
            auth0.loginWithRedirect()
          }}
        >
          Existing User Login
        </Button>

        <Button
          type="button"
          fullWidth
          variant="outlined"
          color="secondary"
          sx={{ mt: 2, mb: 2 }}
          onClick={() => {
            posthog?.capture('signup_button_clicked')
            auth0.loginWithRedirect({ authorizationParams: { screen_hint: 'signup' } })
          }}
        >
          Create New Account
        </Button>
      </Box>
    </Container>
  </>
}

const LoginIfNeeded: React.FC<PropsWithChildren> = ({ children }) => {
  const { isLoading, isAuthenticated, error } = useAuth0()

  if (isLoading) {
    if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Auth0 is Loading...')
    return null // We don't want to flash the login page while we're loading state
  }

  if (error) {
    if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Auth0 had an Error...')
    return <div>Oops... {error.message}</div>
  }

  if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Auth0 is Ready!')

  if (!isAuthenticated) {
    if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Login is required...')
    return <Login />
  }

  if (process.env.NODE_ENV === 'development') console.log('[initAuth0] Login is complete!')
  return <>{children}</>
}

const InitAuth0RequiringLogin: React.FC<PropsWithChildren> = ({ children }) =>
  <Auth0Provider
    domain={REACT_APP_AUTH0_DOMAIN}
    clientId={REACT_APP_AUTH0_CLIENT_ID}
    authorizationParams={{
      redirect_uri: window.location.origin,
      audience: REACT_APP_AUTH0_API_AUDIENCE,
    }}
  >
    <RegisterAuth0TokenProvider />
    <LoginIfNeeded>
      {children}
    </LoginIfNeeded>
  </Auth0Provider>


export default InitAuth0RequiringLogin
